import useEventListener from "@pathwright/ui/src/components/hooks/useEventListener"
import usePath from "@pathwright/web/src/modules/path/hooks/usePath"
import { ORDER_FULFILLED_EVENT } from "@pathwright/web/src/modules/utils/dispatcher"
import { ConnectStores } from "lib/core/store"
import get from "lodash/get"
import React, { useEffect, useReducer, useRef } from "react"
import { serializePathForStore } from "./utils"

export const PathContext = React.createContext()

// handles setting path, step, and lesson in path and step stores based on props
const PathStoreUpdator = ({
  setPath,
  pathStorePath,
  pathSerializationData,
  children
}) => {
  const currentPathRef = useRef(null)
  const { offering } = pathSerializationData

  const [{ pathId, cohortId, forceReload, ensureConsistentRead }, loadPath] =
    useReducer(
      (
        state,
        { pathId, cohortId, forceReload = false, ensureConsistentRead = false }
      ) => {
        if (state.pathId !== pathId || state.cohortId !== cohortId) {
          // determine whether to load from network to avoid a stale cache
          const nextForceReload =
            forceReload ||
            (pathId
              ? // pathId differs from pathStorePath ?
                pathId !== get(pathStorePath, "id", null)
              : cohortId
              ? // cohortId differs from pathStorePath ?
                cohortId !== get(pathStorePath, "offering_id", null) ||
                pathId !== state.pathId // in this case, the cohort is the same but the pathId may differ from state ?
              : false)

          return {
            pathId,
            cohortId,
            forceReload: nextForceReload,
            ensureConsistentRead
          }
        }

        if (
          state.forceReload === forceReload &&
          state.ensureConsistentRead === ensureConsistentRead
        ) {
          // Since next state has not changed from current state, ruturn current state
          // and React will detect that state has not changed and will not trigger rerenders.
          return state
        }

        return {
          ...state,
          forceReload,
          ensureConsistentRead
        }
      },
      // initial state
      {
        pathId: null,
        cohortId: null,
        forceReload: false,
        ensureConsistentRead: false
      }
    )

  const { path, error, loaded, loading, syncing, syncPath, refetch } = usePath({
    pathId,
    cohortId,
    forceReload,
    ensureConsistentRead
  })

  useEffect(() => {
    if (loaded && (forceReload || ensureConsistentRead)) {
      // hacky way of resetting forceReload and ensureConsistentRead
      loadPath({ pathId, cohortId })
    }
  }, [loaded])

  useEffect(() => {
    // clearing the path ref
    if (!pathId && !cohortId) {
      currentPathRef.current = null
    }
  }, [pathId, cohortId])

  // let's not consider the path "loaded" until the correct offering has been loaded
  let offeringLoaded = offering && offering.id === cohortId
  const pathLoaded = loaded && offeringLoaded && !forceReload

  // // NOTE: not wrapping in a useEffect as we NEED the path-store to have latest path data within the same render chain
  // // this does have at least one unwanted side-effect with initial render: "Warning: Cannot update during an existing state transition (such as within `render`). Render methods should be a pure function of props and state.""
  // // For example: drag and drop shows a visual glitch where the items briefly revert before sticking after a drop.
  // if (pathLoaded && currentPathRef.current !== path) {
  //   currentPathRef.current = path
  //   const serializedPath = serializePathForStore({
  //     path,
  //     ...pathSerializationData
  //   })
  //   // set the path in the path store
  //   setPath(serializedPath, true /* forceUpdate */) // always force update
  // }

  useEffect(() => {
    if (
      pathLoaded &&
      (currentPathRef.current !== path || path?.id !== pathStorePath?.id)
    ) {
      currentPathRef.current = path
      const serializedPath = serializePathForStore({
        path,
        ...pathSerializationData
      })
      // set the path in the path store
      setPath(serializedPath, true /* forceUpdate */) // always force update
    }
  }, [pathLoaded, currentPathRef.current, path])

  // Force reloading default path for the user to ensure
  // that an already loaded default path is replaced with the user's own path
  // though the default path will still be loaded for admins / teachers but that should be fine.
  useEventListener(
    ORDER_FULFILLED_EVENT,
    (e) => {
      const { order } = e.detail

      if (loaded && order.order_type === "registration") {
        loadPath({
          pathId: null,
          cohortId: order.order_data.offering_id,
          forceReload: true,
          ensureConsistentRead: true
        })
      }
    },
    [loaded]
  )

  return (
    <PathContext.Provider
      value={{
        path,
        error,
        loaded: pathLoaded,
        loading,
        syncing,
        syncPath,
        refetch,
        pathId,
        cohortId,
        loadPath
      }}
    >
      {children}
    </PathContext.Provider>
  )
}

PathStoreUpdator.displayName = "PathStoreUpdator"

const stores = [
  {
    store: "path",
    watch: false
  },
  {
    store: "step",
    watch: false
  },
  {
    store: "auth",
    watch: ["is_authenticated"]
  },
  {
    store: "resource",
    watch: ["resource", "offering"]
  }
]

const PathProvider = ConnectStores(
  PathStoreUpdator,
  stores,
  {},
  (state, ownProps) => {
    const { resource, offering } = state.resourceStore.state
    const { path: pathStorePath } = state.pathStore.state
    const { setPath } = state.pathStore.action
    const { user, is_authenticated } = state.authStore.state

    return {
      pathStorePath,
      setPath,
      pathSerializationData: {
        resource,
        offering,
        user
      },
      is_authenticated,
      ...ownProps
    }
  }
)

export const withPathProvider = (Component) => (props) =>
  (
    <PathProvider>
      <Component {...props} />
    </PathProvider>
  )

export default PathProvider
